/**
 * Leave day counting (Estonian law).
 *
 * Annual leave and statutory absences are granted in CALENDAR days: weekends
 * count as leave days, only public holidays falling within the period do not
 * consume leave (TLS §59/§68). This is the single rule used everywhere leave
 * days are counted — booking, balances, and display — so the client preview
 * and the server deduction always agree. Pure; pass the relevant company's
 * public-holiday dates.
 */

/** Format a Date (UTC) to 'YYYY-MM-DD'. */
const toIsoDate = (ms: number): string => new Date(ms).toISOString().slice(0, 10)

const DAY_MS = 86_400_000

const toUtc = (date: string): number => {
    const [year, month, day] = date.split('-').map(Number)
    return Date.UTC(year, month - 1, day)
}

/**
 * Calendar days in the inclusive range [start, end] minus any that are public
 * holidays. Weekends count. Returns 0 when start is after end.
 *
 * @param start    'YYYY-MM-DD'
 * @param end      'YYYY-MM-DD'
 * @param holidays public-holiday dates ('YYYY-MM-DD')
 */
export const countLeaveDays = (start: string, end: string, holidays: string[] = []): number => {
    const startMs = toUtc(start)
    const endMs = toUtc(end)
    if (startMs > endMs) return 0

    const holidaySet = new Set(holidays)
    let count = 0
    for (let ms = startMs; ms <= endMs; ms += DAY_MS) {
        if (!holidaySet.has(toIsoDate(ms))) count++
    }
    return count
}

/**
 * Leave days of [start, end] that fall within the given calendar year — the
 * portion a year-scoped balance period actually holds. A year-spanning
 * booking (e.g. 21 Dec – 3 Jan) consumes each year's allowance separately:
 * Humaans counted only the in-period days into its per-year balances, so any
 * refund made against an imported balance must be clipped the same way.
 * Returns 0 when the range lies entirely outside the year.
 *
 * @param start    'YYYY-MM-DD'
 * @param end      'YYYY-MM-DD'
 * @param year     calendar year of the balance period
 * @param holidays public-holiday dates ('YYYY-MM-DD')
 */
export const countLeaveDaysWithinYear = (
    start: string,
    end: string,
    year: number,
    holidays: string[] = [],
): number => {
    const yearStart = `${year}-01-01`
    const yearEnd = `${year}-12-31`
    const from = start > yearStart ? start : yearStart
    const to = end < yearEnd ? end : yearEnd
    return countLeaveDays(from, to, holidays)
}

/**
 * Inclusive calendar-day span of [start, end] — the DURATION of the absence
 * period. Unlike {@link countLeaveDays}, public holidays do NOT shorten it:
 * a full Mon–Sun week is 7 days even when midsummer holidays fall inside it.
 * Used for min-duration / full-week policy validation, where "7 days" means a
 * 7-day span rather than 7 consumed leave days. Returns 0 when start > end.
 *
 * @param start 'YYYY-MM-DD'
 * @param end   'YYYY-MM-DD'
 */
export const countCalendarDays = (start: string, end: string): number => {
    const startMs = toUtc(start)
    const endMs = toUtc(end)
    if (startMs > endMs) return 0
    return Math.round((endMs - startMs) / DAY_MS) + 1
}
